import { ref } from 'vue'

import {
  clearModalAlert,
  showModalError,
  showModalInfo,
  type AlertTone,
} from '@/models/current-student-advanced-standing-form/modalAlert'

type ConfirmDeleteAction = () => Promise<void>

export function useConfirmDeleteController(options: {
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
}) {
  const confirmDeleteModalVisible = ref(false)
  const confirmDeleteError = ref('')
  const confirmDeleteAlertTone = ref<AlertTone>('danger')
  const confirmDeleteMessage = ref('')
  const pendingDeleteAction = ref<ConfirmDeleteAction | null>(null)

  function clearConfirmDeleteError(): void {
    clearModalAlert(confirmDeleteError, confirmDeleteAlertTone)
  }

  function showConfirmDeleteError(message: string): void {
    showModalError(confirmDeleteError, confirmDeleteAlertTone, message)
  }

  function openConfirmDelete(message: string, action: ConfirmDeleteAction): void {
    clearConfirmDeleteError()
    confirmDeleteMessage.value = message
    pendingDeleteAction.value = action
    confirmDeleteModalVisible.value = true
  }

  function cancelConfirmDelete(): void {
    confirmDeleteModalVisible.value = false
  }

  async function runConfirmedDelete(): Promise<void> {
    if (!pendingDeleteAction.value) return

    try {
      showModalInfo(confirmDeleteError, confirmDeleteAlertTone, 'Saving...')
      await pendingDeleteAction.value()
      clearConfirmDeleteError()
      confirmDeleteModalVisible.value = false
      pendingDeleteAction.value = null
    } catch (error) {
      if (options.handleAuthSessionError(error, 'reload')) return

      showConfirmDeleteError(options.getErrorMessage(error))
    }
  }

  return {
    confirmDeleteModalVisible,
    confirmDeleteError,
    confirmDeleteAlertTone,
    confirmDeleteMessage,
    pendingDeleteAction,
    openConfirmDelete,
    cancelConfirmDelete,
    runConfirmedDelete,
    showConfirmDeleteError,
    clearConfirmDeleteError,
  }
}
